import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Plus, Clock, Play, Square, DollarSign, Loader2,
  Trash2, CheckCircle2, Timer, BarChart3, Filter,
  ArrowUpDown, ArrowUp, ArrowDown, Info, FileText,
  Pencil, Settings2, X, User as UserIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import { SavedViews } from '../components/SavedViews';
import { HelpPanel } from '../components/ui/help-panel';
import { timeTrackingHelp } from '../lib/helpContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatHours = (h) => {
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return `${hrs}h ${mins}m`;
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

export default function TimeTracking() {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const [entries, setEntries] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [activeTimer, setActiveTimer] = useState(null);
  const [timerElapsed, setTimerElapsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterBillable, setFilterBillable] = useState('all');
  const [filterStaff, setFilterStaff] = useState('all');
  const [filterClient, setFilterClient] = useState('all');

  // Task type management
  const [typeSettingsOpen, setTypeSettingsOpen] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');

  // Timer state for start form
  const [timerForm, setTimerForm] = useState({ description: '', task_type: '', contact_id: '', billable: true, hourly_rate: '' });
  const timerInterval = useRef(null);
  const wsId = currentWorkspace?.workspace_id;

  // Sort state
  const [timeSort, setTimeSort] = useState('date');
  const [timeSortDir, setTimeSortDir] = useState('desc');
  const handleTimeSort = (field) => {
    if (timeSort === field) setTimeSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setTimeSort(field); setTimeSortDir('desc'); }
  };
  const TimeSortIcon = ({ field }) => {
    if (timeSort !== field) return <ArrowUpDown className="w-3 h-3 text-muted-foreground/40" />;
    return timeSortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-primary" /> : <ArrowDown className="w-3 h-3 text-primary" />;
  };

  // ─── Generate Invoice State ───
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceStep, setInvoiceStep] = useState('select'); // select | customize
  const [invoiceClient, setInvoiceClient] = useState('');
  const [unbilledEntries, setUnbilledEntries] = useState([]);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [invoiceLines, setInvoiceLines] = useState([]);
  const [invoiceStatus, setInvoiceStatus] = useState('draft');
  const [invoiceMemo, setInvoiceMemo] = useState('');
  const [invoiceTerms, setInvoiceTerms] = useState(30);
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [members, setMembers] = useState([]);

  const fetchAll = useCallback(async () => {
    if (!wsId) return;
    try {
      const [entriesRes, typesRes, contactsRes, summaryRes, timerRes, membersRes] = await Promise.all([
        apiFetch(`${API_URL}/api/workspaces/${wsId}/time-entries?limit=100`),
        apiFetch(`${API_URL}/api/workspaces/${wsId}/time-task-types`),
        apiFetch(`${API_URL}/api/contacts?workspace_id=${wsId}&status=active`),
        apiFetch(`${API_URL}/api/workspaces/${wsId}/time-summary`),
        apiFetch(`${API_URL}/api/workspaces/${wsId}/timer`),
        apiFetch(`${API_URL}/api/workspaces/${wsId}/members`),
      ]);
      if (entriesRes.ok) setEntries(await entriesRes.json());
      if (typesRes.ok) setTaskTypes(await typesRes.json());
      if (contactsRes.ok) setContacts(await contactsRes.json());
      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (membersRes.ok) setMembers(await membersRes.json());
      if (timerRes.ok) {
        const t = await timerRes.json();
        if (t.active) setActiveTimer(t);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [wsId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Timer tick
  useEffect(() => {
    if (activeTimer?.start_time) {
      const calcElapsed = () => {
        const start = new Date(activeTimer.start_time);
        const now = new Date();
        setTimerElapsed(Math.floor((now - start) / 1000));
      };
      calcElapsed();
      timerInterval.current = setInterval(calcElapsed, 1000);
      return () => clearInterval(timerInterval.current);
    } else {
      setTimerElapsed(0);
    }
  }, [activeTimer]);

  const formatTimer = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const startTimer = async () => {
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/timer/start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(timerForm),
      });
      if (res.ok) {
        const data = await res.json();
        setActiveTimer(data);
        toast.success('Timer started');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to start timer');
      }
    } catch (e) { toast.error('Failed to start timer'); }
  };

  const stopTimer = async () => {
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/timer/stop`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      if (res.ok) {
        const data = await res.json();
        setActiveTimer(null);
        toast.success(`Timer stopped: ${data.elapsed_hours}h ${data.elapsed_minutes}m logged`);
        fetchAll();
      }
    } catch (e) { toast.error('Failed to stop timer'); }
  };

  const openEditEntry = (entry) => {
    navigate(`/time-tracking/${entry.entry_id}/edit`);
  };

  const openNewEntry = () => {
    navigate('/time-tracking/new');
  };

  // Task type management
  const addTaskType = async () => {
    if (!newTypeName.trim()) return;
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/time-task-types`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newTypeName.trim() }),
      });
      if (res.ok) { setNewTypeName(''); fetchAll(); toast.success('Task type added'); }
      else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
    } catch (e) { toast.error('Failed'); }
  };

  const deleteTaskType = async (typeId) => {
    await apiFetch(`${API_URL}/api/workspaces/${wsId}/time-task-types/${typeId}`, { method: 'DELETE' });
    fetchAll(); toast.success('Task type removed');
  };

  const deleteEntry = async (entryId) => {
    await apiFetch(`${API_URL}/api/workspaces/${wsId}/time-entries/${entryId}`, { method: 'DELETE' });
    fetchAll();
    toast.success('Entry deleted');
  };

  // ─── Generate Invoice Functions ───
  const openInvoiceDialog = () => {
    setInvoiceStep('select');
    setInvoiceClient('');
    setUnbilledEntries([]);
    setSelectedEntries([]);
    setInvoiceLines([]);
    setInvoiceStatus('draft');
    setInvoiceMemo('');
    setInvoiceTerms(30);
    setInvoiceDate(new Date().toISOString().split('T')[0]);
    setInvoiceOpen(true);
  };

  const fetchUnbilled = async (contactId) => {
    setInvoiceClient(contactId);
    setInvoiceLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/time-entries/unbilled?contact_id=${contactId}`);
      if (res.ok) {
        const data = await res.json();
        setUnbilledEntries(data);
        setSelectedEntries(data.map(e => e.entry_id));
      }
    } catch (e) { console.error(e); }
    finally { setInvoiceLoading(false); }
  };

  const toggleEntry = (entryId) => {
    setSelectedEntries(prev => prev.includes(entryId) ? prev.filter(id => id !== entryId) : [...prev, entryId]);
  };
  const toggleAllEntries = () => {
    if (selectedEntries.length === unbilledEntries.length) setSelectedEntries([]);
    else setSelectedEntries(unbilledEntries.map(e => e.entry_id));
  };

  const proceedToCustomize = () => {
    const selected = unbilledEntries.filter(e => selectedEntries.includes(e.entry_id));
    // Group by task_type for initial line items
    const grouped = {};
    selected.forEach(e => {
      const key = e.task_type || 'General';
      if (!grouped[key]) grouped[key] = { hours: 0, amount: 0, rate: 0, entries: [] };
      grouped[key].hours += e.total_hours || 0;
      grouped[key].amount += e.amount || 0;
      grouped[key].entries.push(e.entry_id);
      if (e.hourly_rate) grouped[key].rate = e.hourly_rate;
    });
    const lines = Object.entries(grouped).map(([type, data]) => ({
      description: `${type} — ${data.hours.toFixed(1)} hours`,
      quantity: parseFloat(data.hours.toFixed(2)),
      unit_price: data.rate || (data.hours > 0 ? parseFloat((data.amount / data.hours).toFixed(2)) : 0),
      account_id: '',
      tax_rate: 0,
      source_entry_ids: data.entries,
    }));
    setInvoiceLines(lines);
    setInvoiceMemo(`Time entries: ${selected.length} entries, ${selected.reduce((s, e) => s + (e.total_hours || 0), 0).toFixed(1)} hours`);
    setInvoiceStep('customize');
  };

  const updateInvoiceLine = (i, field, val) => {
    const lines = [...invoiceLines];
    lines[i] = { ...lines[i], [field]: val };
    setInvoiceLines(lines);
  };
  const addInvoiceLine = () => setInvoiceLines([...invoiceLines, { description: '', quantity: 1, unit_price: 0, account_id: '', tax_rate: 0, source_entry_ids: [] }]);
  const removeInvoiceLine = (i) => setInvoiceLines(invoiceLines.filter((_, idx) => idx !== i));

  const invoiceSubtotal = invoiceLines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0), 0);
  const invoiceTax = invoiceLines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0) * ((parseFloat(l.tax_rate) || 0) / 100), 0);
  const invoiceTotal = invoiceSubtotal + invoiceTax;

  const handleGenerateInvoice = async () => {
    if (!invoiceLines.length) { toast.error('Add at least one line item'); return; }
    setGeneratingInvoice(true);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/time-entries/generate-invoice`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: invoiceClient,
          entry_ids: selectedEntries,
          line_items: invoiceLines.map(l => ({ ...l, quantity: parseFloat(l.quantity) || 0, unit_price: parseFloat(l.unit_price) || 0, tax_rate: parseFloat(l.tax_rate) || 0 })),
          invoice_date: invoiceDate,
          payment_terms: invoiceTerms,
          memo: invoiceMemo,
          status: invoiceStatus,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setInvoiceOpen(false);
        fetchAll();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to generate invoice');
      }
    } catch (e) { toast.error('Failed to generate invoice'); }
    finally { setGeneratingInvoice(false); }
  };

  const filtered = entries.filter(e => {
    if (filterType !== 'all' && e.task_type !== filterType) return false;
    if (filterBillable === 'billable' && !e.billable) return false;
    if (filterBillable === 'non_billable' && e.billable) return false;
    if (filterStaff !== 'all' && e.created_by !== filterStaff) return false;
    if (filterClient !== 'all' && e.contact_id !== filterClient) return false;
    return true;
  });

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-32" /><Skeleton className="h-64" /></div>;

  return (
    <div className="space-y-6" data-testid="time-tracking">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Time Tracking</h1>
          <p className="text-muted-foreground text-sm">Track billable and non-billable hours per client</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setTypeSettingsOpen(true)} data-testid="manage-task-types-btn">
            <Settings2 className="w-4 h-4 mr-1" /> Task Types
          </Button>
          <Button variant="outline" onClick={openInvoiceDialog} data-testid="generate-invoice-btn">
            <FileText className="w-4 h-4 mr-1" /> Generate Invoice
          </Button>
          <Button onClick={openNewEntry} data-testid="manual-entry-btn">
            <Plus className="w-4 h-4 mr-1" /> Manual Entry
          </Button>
          <HelpPanel {...timeTrackingHelp} />
        </div>
      </div>

      {/* Live Timer */}
      <Card className={cn("border-2", activeTimer ? "border-emerald-400 dark:border-emerald-600 bg-emerald-50/50 dark:bg-emerald-950/20" : "border-dashed")}>
        <CardContent className="p-4">
          {activeTimer ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center animate-pulse">
                  <Timer className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-3xl font-mono font-bold text-emerald-700 dark:text-emerald-400" data-testid="timer-display">{formatTimer(timerElapsed)}</p>
                  <p className="text-xs text-muted-foreground">{activeTimer.description || 'Timer running'} {activeTimer.task_type && `(${activeTimer.task_type})`}</p>
                </div>
              </div>
              <Button onClick={stopTimer} variant="destructive" size="lg" data-testid="stop-timer-btn">
                <Square className="w-5 h-5 mr-2" /> Stop Timer
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Timer className="w-5 h-5 text-muted-foreground" />
                <span className="font-medium text-sm">Start Timer</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Input placeholder="What are you working on?" value={timerForm.description} onChange={e => setTimerForm({...timerForm, description: e.target.value})} data-testid="timer-desc" />
                <Select value={timerForm.task_type} onValueChange={v => setTimerForm({...timerForm, task_type: v})}>
                  <SelectTrigger data-testid="timer-type"><SelectValue placeholder="Task type" /></SelectTrigger>
                  <SelectContent>{taskTypes.map(t => <SelectItem key={t.type_id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={timerForm.contact_id} onValueChange={v => setTimerForm({...timerForm, contact_id: v})}>
                  <SelectTrigger data-testid="timer-contact"><SelectValue placeholder="Client" /></SelectTrigger>
                  <SelectContent>{contacts.map(c => <SelectItem key={c.contact_id} value={c.contact_id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button onClick={startTimer} className="bg-emerald-600 hover:bg-emerald-700" data-testid="start-timer-btn">
                  <Play className="w-4 h-4 mr-1" /> Start
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Total Hours', value: formatHours(summary.total_hours), color: 'border-l-blue-500', tip: 'Combined billable and non-billable hours logged across all entries', testid: 'total-hours' },
            { label: 'Billable', value: formatHours(summary.billable_hours), color: 'border-l-emerald-500', tip: 'Hours marked as billable — these can be invoiced to clients' },
            { label: 'Non-Billable', value: formatHours(summary.non_billable_hours), color: 'border-l-gray-400', tip: 'Internal or non-chargeable hours (admin, meetings, etc.)' },
            { label: 'Amount', value: formatCurrency(summary.total_amount), color: 'border-l-amber-500', tip: 'Total billable amount based on hours x hourly rate' },
            { label: 'Entries', value: summary.entry_count, color: 'border-l-purple-500', tip: 'Total number of time entries logged' },
          ].map(s => (
            <TooltipProvider key={s.label}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Card className={cn("border-l-4 cursor-help", s.color)}>
                    <CardContent className="p-3">
                      <div className="flex items-center gap-1">
                        <p className="text-xs text-muted-foreground">{s.label}</p>
                        <Info className="w-2.5 h-2.5 text-muted-foreground/40" />
                      </div>
                      <p className="text-xl font-bold" data-testid={s.testid}>{s.value}</p>
                    </CardContent>
                  </Card>
                </TooltipTrigger>
                <TooltipContent><p className="text-xs max-w-[200px]">{s.tip}</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>
      )}

      {/* Saved Views + Filters */}
      <div className="space-y-3">
        <SavedViews
          page="time"
          presets={[
            { name: 'Billable Only', icon: 'dollar', filters: { billable: 'billable', type: 'all', staff: 'all', client: 'all' }, sort: { field: 'date', dir: 'desc' } },
            { name: 'Non-Billable', icon: 'clock', filters: { billable: 'non_billable', type: 'all', staff: 'all', client: 'all' }, sort: { field: 'date', dir: 'desc' } },
            { name: 'Highest Hours', icon: 'sort', filters: { billable: 'all', type: 'all', staff: 'all', client: 'all' }, sort: { field: 'total_hours', dir: 'desc' } },
            { name: 'By Amount', icon: 'dollar', filters: { billable: 'all', type: 'all', staff: 'all', client: 'all' }, sort: { field: 'amount', dir: 'desc' } },
          ]}
          onApply={({ filters, sort }) => {
            if (filters.billable) setFilterBillable(filters.billable);
            if (filters.type) setFilterType(filters.type);
            if (filters.staff) setFilterStaff(filters.staff);
            if (filters.client) setFilterClient(filters.client);
            if (sort.field) { setTimeSort(sort.field); setTimeSortDir(sort.dir || 'desc'); }
          }}
          currentFilters={{ billable: filterBillable, type: filterType, staff: filterStaff, client: filterClient }}
          currentSort={{ field: timeSort, dir: timeSortDir }}
        />
        <div className="flex gap-2 flex-wrap">
        <Select value={filterStaff} onValueChange={setFilterStaff}>
          <SelectTrigger className="w-40" data-testid="filter-staff"><SelectValue placeholder="All Staff" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Staff</SelectItem>
            {members.map(m => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterClient} onValueChange={setFilterClient}>
          <SelectTrigger className="w-44" data-testid="filter-client"><SelectValue placeholder="All Clients" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clients</SelectItem>
            {contacts.map(c => <SelectItem key={c.contact_id} value={c.contact_id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40" data-testid="filter-task-type"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {taskTypes.map(t => <SelectItem key={t.type_id} value={t.name}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterBillable} onValueChange={setFilterBillable}>
          <SelectTrigger className="w-36" data-testid="filter-billable"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="billable">Billable</SelectItem>
            <SelectItem value="non_billable">Non-Billable</SelectItem>
          </SelectContent>
        </Select>
        </div>
      </div>

      {/* Time Entries Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="cursor-pointer select-none" onClick={() => handleTimeSort('date')}><span className="flex items-center gap-1">Date <TimeSortIcon field="date" /></span></TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleTimeSort('task_type')}><span className="flex items-center gap-1">Type <TimeSortIcon field="task_type" /></span></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleTimeSort('contact_name')}><span className="flex items-center gap-1">Client <TimeSortIcon field="contact_name" /></span></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleTimeSort('user_name')}><span className="flex items-center gap-1">Staff <TimeSortIcon field="user_name" /></span></TableHead>
                <TableHead className="text-right cursor-pointer select-none" onClick={() => handleTimeSort('total_hours')}><span className="flex items-center gap-1 justify-end">Hours <TimeSortIcon field="total_hours" /></span></TableHead>
                <TableHead className="text-center">Billable</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right cursor-pointer select-none" onClick={() => handleTimeSort('amount')}><span className="flex items-center gap-1 justify-end">Amount <TimeSortIcon field="amount" /></span></TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length > 0 ? [...filtered].sort((a, b) => {
                let valA = a[timeSort], valB = b[timeSort];
                if (typeof valA === 'string') { valA = (valA || '').toLowerCase(); valB = (valB || '').toLowerCase(); }
                else { valA = valA || 0; valB = valB || 0; }
                if (valA < valB) return timeSortDir === 'asc' ? -1 : 1;
                if (valA > valB) return timeSortDir === 'asc' ? 1 : -1;
                return 0;
              }).map(e => (
                <TableRow key={e.entry_id} data-testid={`entry-${e.entry_id}`}>
                  <TableCell className="text-sm">{e.date}</TableCell>
                  <TableCell className="text-sm max-w-[200px] truncate">{e.description || '-'}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{e.task_type || '-'}</Badge></TableCell>
                  <TableCell className="text-sm">{e.contact_name || '-'}</TableCell>
                  <TableCell>
                    <span className="text-sm flex items-center gap-1.5">
                      <UserIcon className="w-3 h-3 text-muted-foreground" />
                      {e.user_name || '-'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">{formatHours(e.total_hours)}</TableCell>
                  <TableCell className="text-center">
                    {e.billable ? <Badge className="text-[9px] bg-emerald-100 text-emerald-700">Yes</Badge> : <Badge variant="secondary" className="text-[9px]">No</Badge>}
                  </TableCell>
                  <TableCell className="text-right text-sm font-mono">{e.hourly_rate ? `$${e.hourly_rate}` : '-'}</TableCell>
                  <TableCell className="text-right text-sm font-mono font-medium">{e.amount > 0 ? formatCurrency(e.amount) : '-'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-0.5">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditEntry(e)} data-testid={`edit-entry-${e.entry_id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteEntry(e.entry_id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={10} className="text-center py-8">
                  <Clock className="w-10 h-10 mx-auto mb-2 text-muted-foreground/50" />
                  <p className="text-muted-foreground">No time entries yet</p>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Task Types Management Dialog */}
      <Dialog open={typeSettingsOpen} onOpenChange={setTypeSettingsOpen}>
        <DialogContent className="max-w-md" data-testid="task-types-dialog">
          <DialogHeader>
            <DialogTitle>Manage Task Types</DialogTitle>
            <DialogDescription>Add or remove task types used for categorizing time entries</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-1.5">
              {taskTypes.map(t => (
                <div key={t.type_id} className="flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full border text-xs font-medium bg-muted/30" data-testid={`task-type-${t.type_id}`}>
                  {t.name}
                  <button onClick={() => deleteTaskType(t.type_id)} className="w-4 h-4 rounded-full hover:bg-destructive/10 flex items-center justify-center ml-0.5">
                    <X className="w-2.5 h-2.5 text-destructive" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="New task type name"
                value={newTypeName}
                onChange={e => setNewTypeName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addTaskType()}
                data-testid="new-task-type-input"
              />
              <Button size="sm" onClick={addTaskType} disabled={!newTypeName.trim()} data-testid="add-task-type-btn">
                <Plus className="w-3 h-3 mr-1" /> Add
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Generate Invoice Dialog */}
      <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="generate-invoice-dialog">
          <DialogHeader>
            <DialogTitle>
              {invoiceStep === 'select' ? 'Generate Invoice from Time Entries' : 'Customize Invoice'}
            </DialogTitle>
            <DialogDescription>
              {invoiceStep === 'select'
                ? 'Select a client and choose which unbilled time entries to include'
                : 'Review and edit line items before creating the invoice'}
            </DialogDescription>
          </DialogHeader>

          {/* Step 1: Select Client & Entries */}
          {invoiceStep === 'select' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select value={invoiceClient} onValueChange={fetchUnbilled}>
                  <SelectTrigger data-testid="invoice-client-select"><SelectValue placeholder="Select a client..." /></SelectTrigger>
                  <SelectContent>
                    {contacts.filter(c => c.contact_type === 'customer').map(c => (
                      <SelectItem key={c.contact_id} value={c.contact_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {invoiceLoading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>}

              {invoiceClient && !invoiceLoading && unbilledEntries.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p>No unbilled time entries for this client</p>
                  <p className="text-xs mt-1">All billable entries have been invoiced</p>
                </div>
              )}

              {unbilledEntries.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>{selectedEntries.length} of {unbilledEntries.length} entries selected</Label>
                    <Button variant="ghost" size="sm" onClick={toggleAllEntries} data-testid="toggle-all-entries">
                      {selectedEntries.length === unbilledEntries.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  <div className="max-h-[40vh] overflow-y-auto rounded-lg border">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead className="w-10"></TableHead>
                          <TableHead className="text-xs">Date</TableHead>
                          <TableHead className="text-xs">Description</TableHead>
                          <TableHead className="text-xs">Type</TableHead>
                          <TableHead className="text-xs text-right">Hours</TableHead>
                          <TableHead className="text-xs text-right">Rate</TableHead>
                          <TableHead className="text-xs text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {unbilledEntries.map(e => (
                          <TableRow key={e.entry_id} className={cn("cursor-pointer", !selectedEntries.includes(e.entry_id) && "opacity-50")} onClick={() => toggleEntry(e.entry_id)}>
                            <TableCell className="text-center">
                              <input type="checkbox" checked={selectedEntries.includes(e.entry_id)} onChange={() => toggleEntry(e.entry_id)} className="rounded" />
                            </TableCell>
                            <TableCell className="text-xs">{e.date}</TableCell>
                            <TableCell className="text-xs max-w-[180px] truncate">{e.description || '-'}</TableCell>
                            <TableCell><Badge variant="outline" className="text-[9px]">{e.task_type || '-'}</Badge></TableCell>
                            <TableCell className="text-xs text-right font-mono">{formatHours(e.total_hours)}</TableCell>
                            <TableCell className="text-xs text-right font-mono">{e.hourly_rate ? `$${e.hourly_rate}` : '-'}</TableCell>
                            <TableCell className="text-xs text-right font-mono font-medium">{e.amount > 0 ? formatCurrency(e.amount) : '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                    <span className="text-sm font-medium">
                      {selectedEntries.length} entries, {unbilledEntries.filter(e => selectedEntries.includes(e.entry_id)).reduce((s, e) => s + (e.total_hours || 0), 0).toFixed(1)} hours
                    </span>
                    <span className="text-sm font-bold">
                      {formatCurrency(unbilledEntries.filter(e => selectedEntries.includes(e.entry_id)).reduce((s, e) => s + (e.amount || 0), 0))}
                    </span>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setInvoiceOpen(false)}>Cancel</Button>
                <Button onClick={proceedToCustomize} disabled={selectedEntries.length === 0} data-testid="proceed-customize-btn">
                  Next: Customize Line Items
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 2: Customize Line Items */}
          {invoiceStep === 'customize' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>Invoice Date</Label>
                  <Input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} data-testid="invoice-date" />
                </div>
                <div className="space-y-2">
                  <Label>Payment Terms (days)</Label>
                  <Input type="number" value={invoiceTerms} onChange={e => setInvoiceTerms(parseInt(e.target.value) || 30)} />
                </div>
                <div className="space-y-2">
                  <Label>Create As</Label>
                  <Select value={invoiceStatus} onValueChange={setInvoiceStatus}>
                    <SelectTrigger data-testid="invoice-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft (review first)</SelectItem>
                      <SelectItem value="posted">Posted (ready to send)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Memo</Label>
                <Input value={invoiceMemo} onChange={e => setInvoiceMemo(e.target.value)} placeholder="Optional memo" />
              </div>

              {/* Editable Line Items */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">Line Items</Label>
                  <Button size="sm" variant="outline" onClick={addInvoiceLine}><Plus className="w-3 h-3 mr-1" />Add Line</Button>
                </div>
                <div className="space-y-2">
                  {invoiceLines.map((line, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-5">
                        {i === 0 && <Label className="text-[10px]">Description</Label>}
                        <Input className="h-9 text-xs" value={line.description} onChange={e => updateInvoiceLine(i, 'description', e.target.value)} placeholder="Line item description" />
                      </div>
                      <div className="col-span-2">
                        {i === 0 && <Label className="text-[10px]">Qty (hours)</Label>}
                        <Input className="h-9 text-xs font-mono" type="number" min="0" step="0.01" value={line.quantity} onChange={e => updateInvoiceLine(i, 'quantity', e.target.value)} />
                      </div>
                      <div className="col-span-2">
                        {i === 0 && <Label className="text-[10px]">Rate ($/hr)</Label>}
                        <Input className="h-9 text-xs font-mono" type="number" min="0" step="0.01" value={line.unit_price} onChange={e => updateInvoiceLine(i, 'unit_price', e.target.value)} />
                      </div>
                      <div className="col-span-2 text-right">
                        {i === 0 && <Label className="text-[10px]">Amount</Label>}
                        <p className="h-9 flex items-center justify-end text-xs font-mono font-medium">
                          {formatCurrency((parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0))}
                        </p>
                      </div>
                      <div className="col-span-1">
                        {invoiceLines.length > 1 && (
                          <Button size="icon" variant="ghost" className="h-9 w-9 text-destructive" onClick={() => removeInvoiceLine(i)}><Trash2 className="w-3 h-3" /></Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-6 pt-3 border-t text-sm">
                  <span className="text-muted-foreground">Subtotal: <span className="font-mono font-medium text-foreground">{formatCurrency(invoiceSubtotal)}</span></span>
                  {invoiceTax > 0 && <span className="text-muted-foreground">Tax: <span className="font-mono font-medium text-foreground">{formatCurrency(invoiceTax)}</span></span>}
                  <span className="font-semibold">Total: <span className="font-mono">{formatCurrency(invoiceTotal)}</span></span>
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setInvoiceStep('select')}>Back</Button>
                <Button onClick={handleGenerateInvoice} disabled={generatingInvoice || invoiceLines.length === 0} data-testid="create-invoice-btn" className="bg-emerald-600 hover:bg-emerald-700">
                  {generatingInvoice ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                  Create Invoice ({invoiceStatus === 'draft' ? 'Draft' : 'Posted'})
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
